import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getLocationsApi } from '../utils/Api/Authantication';
import { GetProfileInfoApi } from '../utils/Api/Settings';
import { settings } from './settingsSlice';
import store from './store';
import { useDispatch, useSelector } from 'react-redux';
import { user } from './permissionSlice';
import { RegisterLoginEmailBlockingModal } from './RegisterLoginEmailBlockingModal';

const DataContext = createContext({});

export const DataProvider = ({ children }) => {
    const [locations, setLocations] = useState([]);
    const [storeSettings, setStoreSettings] = useState(null);
    const [registerLoginEmailRequired, setRegisterLoginEmailRequired] = useState(false);
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user?.data);
    const isNewLogin = process.env.REACT_APP_USE_NEW_LOGIN === 'true' || process.env.REACT_APP_USE_NEW_LOGIN === 'TRUE';

    const getLocations = useCallback(async () => {
        try {
            const resp = await getLocationsApi();
            setLocations(resp?.data);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    }, []);

    const updateOutletSettings = useCallback(async () => {
        try {
            const res = await GetProfileInfoApi();
            const {
                schedules,
                holidays,
                employees,
                is_individual_opening_hour,
                employees_opening_hour,
                mac_addresses,
                policy,
            } = res.data.data;

            const onlineBookingSetting = res.data.data?.settings?.find(
                (setting) => setting.settingCategory === 'outlet' && setting.settingName === 'OnlineBooking',
            );
            const calendarSetting = res.data.data?.settings?.find(
                (setting) => setting.settingCategory === 'outlet' && setting.settingName === 'calendar',
            );
            const parsedCalendarSetting = calendarSetting ? JSON.parse(calendarSetting?.value) : null;
            const parsedOnlineBookingSetting = onlineBookingSetting ? JSON.parse(onlineBookingSetting?.value) : null;

            let profileData = { ...res.data.data };
            delete profileData?.schedules;
            delete profileData?.holidays;

            const dashboardAidHandoff =
                typeof window !== 'undefined' && Boolean(new URLSearchParams(window.location.search).get('aid'));
            const betaBaseUrl = (process.env.REACT_APP_BETA_URL || '').trim();
            // enable_beta → hand off session to beta admin (same query shape App.getdata accepts).
            // Skip when receiving an inbound ?aid= handoff / from_dashboard to avoid redirect loops.
            const handOffToBeta = () => {
                const authToken = localStorage.getItem('auth_token');
                const employeeId = localStorage.getItem('employee_id');
                const employeeRole = localStorage.getItem('employee_role');
                if (!(authToken && employeeId != null && employeeId !== '' && employeeRole)) {
                    return false;
                }
                const base = betaBaseUrl.replace(/\/$/, '');
                const params = new URLSearchParams({
                    aid: authToken,
                    eid: String(employeeId),
                    er: String(employeeRole),
                    beta_redirect: true,
                });
                const searchParams = new URLSearchParams(window.location.search);
                const sessionSettings = store.getState()?.settings?.data;
                const fromDashboard = localStorage.getItem('from_dashboard') === 'true';
                const fromApp = sessionSettings?.from_app === true || searchParams.get('from_app') === 'true';
                if (fromDashboard) {
                    params.set('from_dashboard', 'true');
                }
                if (fromApp) {
                    params.set('from_app', 'true');
                }
                window.location.href = `${base}?${params.toString()}`;
                return true;
            };
            if (profileData?.enable_beta && betaBaseUrl && typeof window !== 'undefined') {
                if (handOffToBeta()) {
                    return;
                }
            }

            const rawLoginEmail = profileData?.login_email;
            const needsRegisterLoginEmail =
                rawLoginEmail == null || (typeof rawLoginEmail === 'string' && rawLoginEmail.trim() === '');
            // Do not block JWT handoff on /?aid=… — App.getdata navigates away next; modal can show after search is cleared.
            setRegisterLoginEmailRequired(needsRegisterLoginEmail && !dashboardAidHandoff);

            const posSetting = profileData?.settings?.find(
                (setting) =>
                    setting.settingCategory === 'pos_settings' && setting.settingName === 'pos_general_settings',
            );

            const setting = {
                OnlineBooking: parsedOnlineBookingSetting,
                calendar: parsedCalendarSetting,
                schedule: schedules,
                outlet_holidays: holidays,
                profile: profileData,
                is_individual_opening_hour,
                employees_opening_hour: employees_opening_hour,
                employees,
                macAddress: mac_addresses,
                policy: policy,
            };

            if (posSetting && Object.keys(posSetting)?.length > 0) {
                setting.posSetting = { ...posSetting, value: JSON.parse(posSetting?.value) };
            }

            // Dispatch settings to Redux store
            dispatch(settings(setting));

            // Force a small delay to ensure Redux state is updated
            await new Promise((resolve) => setTimeout(resolve, 50));

            // Update localStorage with the latest settings for immediate access
            const currentSettings = JSON.parse(localStorage.getItem('persist:settings') || '{}');
            const existingData = JSON.parse(currentSettings.data || '{}');
            const updatedSettings = {
                ...currentSettings,
                data: JSON.stringify({ ...existingData, ...setting }),
            };
            localStorage.setItem('persist:settings', JSON.stringify(updatedSettings));

            const employee = localStorage.getItem('employee_id');
            employees.find((emp) => {
                if (emp.id === Number(employee)) {
                    const posSettings = res?.data?.data?.settings?.find(
                        (setting) =>
                            setting.settingCategory === 'pos_settings' &&
                            setting.settingName === 'pos_general_settings',
                    );
                    const parsedPosSettings = posSettings ? JSON.parse(posSettings?.value) : null;
                    const employeePOSPermissions = parsedPosSettings?.employeePermissions?.[emp?.id] || null;
                    dispatch(user({ ...emp, pos_settings: employeePOSPermissions }));
                    return true;
                }
                return false;
            });

            setStoreSettings(res.data.data);
        } catch (error) {
            console.error('error', error);
            setRegisterLoginEmailRequired(false);
        }
    }, [dispatch, isNewLogin]);

    const refreshSettings = useCallback(async () => {
        try {
            // Ensure we have auth token before making API calls
            const authToken = localStorage.getItem('auth_token');
            if (!authToken) {
                console.warn('No auth token found, skipping refreshSettings');
                return;
            }

            await updateOutletSettings();
            await getLocations();
        } catch (error) {
            console.error('Error in refreshSettings:', error);
        }
    }, [updateOutletSettings, getLocations]);

    useEffect(() => {
        // Must not call profile APIs before App.getdata() copies ?aid= into localStorage.
        // Otherwise the first request is unauthenticated; a 403 response clears storage and
        // redirects to "/", which strips the handoff query string (breaks embed / dashboard links).
        const hasDashboardAidHandoff =
            typeof window !== 'undefined' && Boolean(new URLSearchParams(window.location.search).get('aid'));
        if (hasDashboardAidHandoff) {
            return;
        }
        // Logged-out `/` still mounts this provider. Profile/accounts calls 403, and the
        // interceptor assigns window.location.href = '/', which reloads the page forever.
        if (!localStorage.getItem('auth_token')) {
            return;
        }
        updateOutletSettings();
        getLocations();
    }, [updateOutletSettings, getLocations]);

    const isFromDashboard = typeof window !== 'undefined' && localStorage.getItem('from_dashboard') === 'true';

    return (
        <DataContext.Provider value={{ locations, storeSettings, refreshSettings }}>
            {children}
            {isNewLogin && (
                <RegisterLoginEmailBlockingModal
                    open={!isFromDashboard && registerLoginEmailRequired && currentUser?.role === 'ADMIN'}
                    onVerified={refreshSettings}
                />
            )}
        </DataContext.Provider>
    );
};

export const useData = () => useContext(DataContext);
